<template>
  <div class="ad-activity">
    <p class="ad-activity__title mb-0">Activity</p>
    <v-divider></v-divider>

    <!-- draft: the record has no history until it exists on the server -->
    <div v-if="!assignmentId" class="ad-activity__body">
      <div class="ad-activity__placeholder">
        <div>
          <p class="text-center mb-0">There is no activity yet.</p>
          <p class="text-center mb-0">Publish the assignment to view</p>
          <p class="text-center mb-0">its history.</p>
        </div>
      </div>
    </div>

    <!-- history feed: oldest at the top, newest at the bottom, older pages pulled
         in as the user scrolls back up — the same flow as the task form -->
    <div v-else class="ad-activity__body" ref="feed" @scroll="handleScroll">
      <div v-if="loading" class="ad-activity__placeholder">
        <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
      </div>

      <div v-else-if="error" class="ad-activity__placeholder">
        <div>
          <p class="text-center mb-0">{{ errorMessage }}</p>
          <p
            v-if="canRetry"
            class="text-center mb-0 ad-activity__placeholder--action"
            @click="loadFirstPage"
          >
            Try again
          </p>
        </div>
      </div>

      <div v-else-if="!groups.length" class="ad-activity__placeholder">
        <div>
          <p class="text-center mb-0">No activity has been recorded</p>
          <p class="text-center mb-0">for this assignment yet.</p>
        </div>
      </div>

      <template v-else>
        <div v-if="loadingOlder" class="text-center py-2">
          <v-progress-circular indeterminate color="primary" size="22"></v-progress-circular>
        </div>

        <div
          v-for="group in groups"
          :key="group.key"
          class="ad-activity__entry"
        >
          <div class="d-flex align-center justify-space-between w-100">
            <p class="ad-activity__entry--author mb-0">{{ group.performedBy }}</p>
            <p class="ad-activity__entry--timestamp mb-0">{{ group.performedAt }}</p>
          </div>
          <div
            v-for="(line, index) in group.lines"
            :key="index"
            class="d-flex align-start mt-1"
          >
            <div class="ad-activity__entry--dot"></div>
            <p class="ad-activity__entry--text mb-0" :title="line.title">
              {{ line.text }}
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getAssignmentActivities } from "@/utils/newDbUtils.js";
import { OFFICE_TIME_ZONE } from "@/utils/assignmentFormUtils.js";

// Rows per request. The API's own default, kept explicit so the paging maths
// below reads plainly.
const PAGE_SIZE = 25;

// The activity types that describe the record as a whole and carry no field
// values of their own.
const STANDALONE_LABELS = {
  CREATED: "Created this assignment",
  ARCHIVED: "Archived this assignment",
  RESTORED: "Restored this assignment",
};

// Longer than this and a bullet stops being scannable, so the row shows the head
// of the value and the tooltip carries the whole of it. History values are stored
// truncated at 2000 characters, which is far past readable in a 420px panel.
const MAX_VALUE_LENGTH = 160;

export default {
  name: "ADFormActivity",

  props: {
    // The saved assignment's record id. Null on a draft, which is what the
    // "no activity yet" state is for.
    assignmentId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      // Oldest first, i.e. display order — the API answers newest first.
      rows: [],
      // The highest page index fetched so far.
      page: 0,
      totalPages: 0,
      loading: false,
      loadingOlder: false,
      error: false,
      errorMessage: "",
      canRetry: true,
    };
  },

  computed: {
    hasOlderPages() {
      return this.page < this.totalPages - 1;
    },
    // One card per save. The API writes a row per changed field, so rows sharing
    // a performer and a timestamp are one action and read as a single bulleted
    // entry — the shape the task form's activity cards use.
    groups() {
      const groups = [];
      this.rows.forEach((row) => {
        const key = `${row.performedById ?? "system"}|${row.performedAt ?? ""}`;
        const last = groups[groups.length - 1];
        if (last && last.key === key) {
          last.lines.push(this.describe(row));
          return;
        }
        groups.push({
          // The same person saving twice in the same second would repeat the
          // key, so the first row's id makes it unique for :key.
          key: `${key}|${row.id}`,
          // A scheduled job or an import leaves no performer behind.
          performedBy: row.performedByName || "System",
          performedAt: this.formatTimestamp(row.performedAt),
          lines: [this.describe(row)],
        });
      });
      return groups;
    },
  },

  watch: {
    assignmentId() {
      this.loadFirstPage();
    },
  },

  methods: {
    async loadFirstPage() {
      if (!this.assignmentId) return;
      this.loading = true;
      this.error = false;
      this.errorMessage = "";
      this.rows = [];
      this.page = 0;
      this.totalPages = 0;

      const resp = await getAssignmentActivities(this.assignmentId, {
        page: 0,
        size: PAGE_SIZE,
      });
      const rows = this.readPage(resp);
      if (rows === null) {
        this.applyError(resp);
        this.loading = false;
        return;
      }
      this.rows = rows;
      this.loading = false;
      // The newest entry sits at the bottom, so that is where the panel opens.
      this.$nextTick(this.scrollToBottom);
    },

    async loadOlderPage() {
      if (this.loading || this.loadingOlder || !this.hasOlderPages) return;
      this.loadingOlder = true;

      const feed = this.$refs.feed;
      const heightBefore = feed ? feed.scrollHeight : 0;
      const nextPage = this.page + 1;
      const resp = await getAssignmentActivities(this.assignmentId, {
        page: nextPage,
        size: PAGE_SIZE,
      });
      const rows = this.readPage(resp);

      if (rows === null) {
        // What is already on screen stays there — a failed older page must not
        // replace a readable history with an error. Treating it as the end of
        // the list is what stops the next scroll from retrying forever.
        console.error(
          "Failed to load an older page of assignment activity:",
          resp?.status
        );
        this.totalPages = nextPage;
      } else {
        this.rows = [...rows, ...this.rows];
        this.page = nextPage;
      }
      this.loadingOlder = false;

      // Keep the row the user was reading under the cursor.
      this.$nextTick(() => {
        if (!feed) return;
        feed.scrollTop = feed.scrollHeight - heightBefore;
      });
    },

    // Reads the Spring page off the response and returns its rows in display
    // order, or null when the request failed.
    readPage(resp) {
      if (!resp || resp.status > 204 || !resp.data) return null;
      this.totalPages = resp.data.totalPages ?? 0;
      return [...(resp.data.content || [])].reverse();
    },

    applyError(resp) {
      this.error = true;
      if (resp?.status === 403) {
        this.errorMessage = "You don't have access to this assignment's history.";
        this.canRetry = false;
      } else if (resp?.status === 404) {
        this.errorMessage = "No history was found for this assignment.";
        this.canRetry = false;
      } else {
        this.errorMessage = "The activity could not be loaded.";
        this.canRetry = true;
      }
    },

    handleScroll(event) {
      if (event.target.scrollTop <= 0) this.loadOlderPage();
    },

    scrollToBottom() {
      const feed = this.$refs.feed;
      if (!feed) return;
      feed.scrollTop = feed.scrollHeight;
    },

    // One bullet: what changed, and the untruncated value for the tooltip.
    describe(row) {
      const label = row.fieldLabel || row.field || "";

      switch (row.activityType) {
        case "CREATED":
        case "ARCHIVED":
        case "RESTORED":
          return { text: STANDALONE_LABELS[row.activityType], title: "" };
        case "STAFF_ASSIGNED":
          return {
            text: `${label} assigned: ${this.formatValue(row.newValue)}`,
            title: "",
          };
        case "STAFF_UNASSIGNED":
          return {
            text: `${label} unassigned: ${this.formatValue(row.oldValue)}`,
            title: "",
          };
        default: {
          // A type this build doesn't know about still has to render as
          // something — never a blank row.
          if (!label && row.oldValue == null && row.newValue == null)
            return { text: this.prettifyType(row.activityType), title: "" };

          const oldValue = this.formatValue(row.oldValue);
          const newValue = this.formatValue(row.newValue);
          const full = `${label}: ${oldValue} → ${newValue}`;
          const text = `${label}: ${this.clamp(oldValue)} → ${this.clamp(newValue)}`;
          return { text, title: text === full ? "" : full };
        }
      }
    },

    prettifyType(activityType) {
      const words = String(activityType || "CHANGED").replace(/_/g, " ").toLowerCase();
      return words.charAt(0).toUpperCase() + words.slice(1);
    },

    clamp(value) {
      const text = String(value);
      return text.length > MAX_VALUE_LENGTH
        ? `${text.slice(0, MAX_VALUE_LENGTH)}…`
        : text;
    },

    // History values are stored as text in a machine-readable form (ISO dates,
    // plain-string decimals, true/false), so they are read back into something
    // displayable here.
    formatValue(value) {
      if (value === null || value === undefined || value === "") return "—";

      const raw = String(value);
      if (raw === "true") return "Yes";
      if (raw === "false") return "No";

      // 2026-08-14, optionally with a time. Rendered in UTC on purpose: the
      // stored value has no zone, so shifting it would show the user a date
      // nobody ever typed.
      const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?$/);
      if (isoDate) {
        const [, year, month, day, hour, minute] = isoDate;
        const date = new Date(
          Date.UTC(+year, +month - 1, +day, hour ? +hour : 0, minute ? +minute : 0)
        );
        const options = {
          day: "2-digit",
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        };
        if (hour)
          Object.assign(options, {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        return this.tidyDateString(
          new Intl.DateTimeFormat("en-US", options).format(date)
        );
      }

      // 17:00:00 → 5:00pm
      const isoTime = raw.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
      if (isoTime) {
        const hour = Number(isoTime[1]);
        const suffix = hour < 12 ? "am" : "pm";
        return `${hour % 12 === 0 ? 12 : hour % 12}:${isoTime[2]}${suffix}`;
      }

      // 120.0 → 120, and 0.125 is left alone.
      if (/^-?\d+\.\d+$/.test(raw)) return String(Number(raw));

      return raw;
    },

    // The API sends ISO-8601 with an offset; the rest of this app's payloads send
    // a [year, month, day, hour, minute] array, so accept both.
    toDate(value) {
      if (!value) return null;
      if (Array.isArray(value)) {
        const [year, month, day, hour = 0, minute = 0] = value;
        return new Date(Date.UTC(year, month - 1, day, hour, minute));
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    },

    // Office time, in the same format the task form's activity stamps use.
    formatTimestamp(value) {
      const date = this.toDate(value);
      if (!date) return "";
      return this.tidyDateString(
        new Intl.DateTimeFormat("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: OFFICE_TIME_ZONE,
        }).format(date)
      );
    },

    tidyDateString(formatted) {
      return formatted.replace(",", "").replace(" AM", "am").replace(" PM", "pm");
    },
  },

  mounted() {
    this.loadFirstPage();
  },
};
</script>

<style lang="scss" scoped>
.ad-activity {
  font-family: 'Roboto', sans-serif !important;
  background-color: #fff;
  // Matches the hierarchy panel and the details column, so the three end level
  // and the modal itself never needs a scrollbar — the feed scrolls inside its
  // own body.
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__title {
    font-size: 20px;
    font-weight: 400;
    line-height: 26px;
    color: #656F7D;
    padding: 16px 20px;
  }

  &__body {
    // The panel's only scroll container.
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 10px 7px 20px 7px;
    background-color: #FBFBFC;

    &::-webkit-scrollbar {
      width: 10px;
    }
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    &::-webkit-scrollbar-thumb {
      background: #ADB3BD;
      border-radius: 8px;
    }
    & {
      scrollbar-width: thin;
      scrollbar-color: #ADB3BD #f1f1f1;
    }
  }

  &__placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    & p {
      font-family: Roboto;
      font-size: 16px;
      font-weight: 400;
      line-height: 18px;
      text-align: center;
      color: #B4B4B4;
    }
    &--action {
      color: #1976D2 !important;
      margin-top: 8px !important;
      &:hover {
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }

  &__entry {
    padding: 5px 13px;
    margin-bottom: 10px;

    &--author {
      font-size: 12px;
      font-weight: 400;
      color: #949494;
    }

    &--timestamp {
      font-size: 12px;
      font-weight: 500;
      color: #949494;
      white-space: nowrap;
      padding-left: 8px;
    }

    &--dot {
      width: 3px;
      height: 3px;
      background-color: #000;
      border-radius: 50%;
      margin-right: 7px;
      margin-top: 8px;
      flex-shrink: 0;
    }

    &--text {
      font-size: 14px;
      line-height: 20px;
      font-weight: 300;
      color: #000;
      // A history value can be a URL or an unbroken run of text; it wraps rather
      // than widening the panel.
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }
}
</style>
